import { ACTIONTYPES } from '@carm/shared/components/table/table-ag-grid/models/table-actions-model';
import { ProgramTypes } from '@carm/_core';
import { IDisplayAction, IHoldHybrisCaseStatus } from './enrol-program-api.model';

export enum ProgramEnrolmentType {
  Importer = 'importer',
  CustomsBroker = 'customs_broker',
  CarrierFreightForwarder = 'carrier_freight_forward',
  Warehouse = 'warehouse',
  DutyFreeShop = 'duty_free_shop',
  Exporter = 'exporter',
  ThirdParty = 'third_party',
  SubProgram = 'sub_program',
  Bonded = 'bonded'
}

export enum WarehouseSubProgramType {
  CustomsBondedWarehouse = 'Customs bonded warehouse',
  SufferanceWarehouse = 'Sufferance warehouse',
}

export enum ThirdPartySubProgramType {
  EDIServiceProvider = 'EDI service provider',
  SuretyCompany = 'Surety company',
  ForeignEntity = 'Foreign entity',
  TradeAccountant = 'Trade accountant',
  TradeConsultant = 'Trade consultant',
  TradeLawyer = 'Trade lawyer',
}

export enum RppEnrolmentStatus {
  NotEnrolled = '-1',
  Enrolled = '1',
  Suspended = '2',
  Cancelled = '3',
  Revoked = '4'
}

export const mapProgramTypeToThirdPartyServiceType = new Map<ProgramTypes, ThirdPartySubProgramType>([
  [ProgramTypes.ThirdPartyServiceProviderEDI, ThirdPartySubProgramType.EDIServiceProvider],
  [ProgramTypes.ThirdPartyServiceProviderAcountant, ThirdPartySubProgramType.TradeAccountant],
  [ProgramTypes.ThirdPartyServiceProviderConsultant, ThirdPartySubProgramType.TradeConsultant],
  [ProgramTypes.ThirdPartyServiceProvider, ThirdPartySubProgramType.ForeignEntity],
  [ProgramTypes.ThirdPartyServiceProviderLawyer, ThirdPartySubProgramType.TradeLawyer],
  [ProgramTypes.ThirdPartyServiceProviderSurety, ThirdPartySubProgramType.SuretyCompany],
]);

export enum ThirdPartyEdiServiceType {
  AcceleratedCommercialReleaseOperations = 'ZACR',
  CommercialAccountingDeclaration = 'ZCAD',
  DailyNoticeAndStatementOfAccount = 'ZARL',
  G7Edi = 'ZG7',
  MarineExportReporting = 'ZMER',
  ReleaseNotificationSystem = 'ZRNS',
  AdvanceCommercialInformation = 'ZACI',
  IntegratedImportDeclaration = 'ZSWI'
}

export enum ThirdPartyS4EdiServiceType {
  AcceleratedCommercialReleaseOperations = '104',
  CommercialAccountingDeclaration = '108',
  DailyNoticeAndStatementOfAccount = '103',
  G7Edi = '105',
  MarineExportReporting = '106',
  ReleaseNotificationSystem = '107',
  AdvanceCommercialInformation = '101',
  IntegratedImportDeclaration = '102'
}

export enum ThirdPartyConnectionMethod {
  CustomsInternetGateway = '101',
  DirectConnectToCBSA = '102',
  ThirdPartyServiceProvider = '103',
  WebService = '104'
}

export const thirdPartyTypeToS4EnrolmentTypesMap = new Map<ThirdPartyEdiServiceType, ThirdPartyS4EdiServiceType>([
  [ThirdPartyEdiServiceType.AcceleratedCommercialReleaseOperations, ThirdPartyS4EdiServiceType.AcceleratedCommercialReleaseOperations],
  [ThirdPartyEdiServiceType.CommercialAccountingDeclaration, ThirdPartyS4EdiServiceType.CommercialAccountingDeclaration],
  [ThirdPartyEdiServiceType.DailyNoticeAndStatementOfAccount, ThirdPartyS4EdiServiceType.DailyNoticeAndStatementOfAccount],
  [ThirdPartyEdiServiceType.G7Edi, ThirdPartyS4EdiServiceType.G7Edi],
  [ThirdPartyEdiServiceType.MarineExportReporting, ThirdPartyS4EdiServiceType.MarineExportReporting],
  [ThirdPartyEdiServiceType.ReleaseNotificationSystem, ThirdPartyS4EdiServiceType.ReleaseNotificationSystem],
  [ThirdPartyEdiServiceType.AdvanceCommercialInformation, ThirdPartyS4EdiServiceType.AdvanceCommercialInformation],
  [ThirdPartyEdiServiceType.IntegratedImportDeclaration, ThirdPartyS4EdiServiceType.IntegratedImportDeclaration],
]);

export const thirdPartyS4EdiServiceTypeToThirdPartyEdiServiceType = new Map<ThirdPartyS4EdiServiceType, ThirdPartyEdiServiceType>([
  [ThirdPartyS4EdiServiceType.AcceleratedCommercialReleaseOperations, ThirdPartyEdiServiceType.AcceleratedCommercialReleaseOperations],
  [ThirdPartyS4EdiServiceType.CommercialAccountingDeclaration, ThirdPartyEdiServiceType.CommercialAccountingDeclaration],
  [ThirdPartyS4EdiServiceType.DailyNoticeAndStatementOfAccount, ThirdPartyEdiServiceType.DailyNoticeAndStatementOfAccount],
  [ThirdPartyS4EdiServiceType.G7Edi, ThirdPartyEdiServiceType.G7Edi],
  [ThirdPartyS4EdiServiceType.MarineExportReporting, ThirdPartyEdiServiceType.MarineExportReporting],
  [ThirdPartyS4EdiServiceType.ReleaseNotificationSystem, ThirdPartyEdiServiceType.ReleaseNotificationSystem],
  [ThirdPartyS4EdiServiceType.AdvanceCommercialInformation, ThirdPartyEdiServiceType.AdvanceCommercialInformation],
  [ThirdPartyS4EdiServiceType.IntegratedImportDeclaration, ThirdPartyEdiServiceType.IntegratedImportDeclaration],
]);

export const s4EdiServiceTypeToFileTemplateMetadataMap = new Map<ThirdPartyEdiServiceType, string>([
  [ThirdPartyEdiServiceType.AcceleratedCommercialReleaseOperations, 'accelerated_commercial_release_operations_support_system'],
  [ThirdPartyEdiServiceType.CommercialAccountingDeclaration, 'commercial_accounting_declaration'],
  [ThirdPartyEdiServiceType.DailyNoticeAndStatementOfAccount, 'daily_notice_and_SOA'],
  [ThirdPartyEdiServiceType.G7Edi, 'g7_edi'],
  [ThirdPartyEdiServiceType.MarineExportReporting, 'marine_export_reporting'],
  [ThirdPartyEdiServiceType.ReleaseNotificationSystem, 'release_notification_system'],
  [ThirdPartyEdiServiceType.AdvanceCommercialInformation, 'advance_commercial_information'],
  [ThirdPartyEdiServiceType.IntegratedImportDeclaration, 'integrated_import_declaration'],
]);

export enum ThirdPartyForeignEntityType {
  Exporter = 'Exporter',
  Producer = 'Producer',
  Vendor = 'Vendor',
  ShippingAgent = 'Shipping Agent',
  MarineAgent = 'Marine Agent',
}

export enum ThirdPartyForeignEntityTypeCodes {
  Exporter = 'ZFEE',
  Producer = 'ZFEP',
  Vendor = 'ZFEV',
  ShippingAgent = 'ZFES',
  MarineAgent = 'ZFEM',
  Cooperative = 'ZFEC'
}

export enum EdiServiceProviderCategoryType {
  EdiConnection = 'edi_connection',
  EdiSoftware = 'edi_software'
}

export enum ImporterSubProgramType {
  Commercial = 'Commercial',
  NonCommercial = 'Non-Commercial',
}

export enum ImporterType {
  Commercial = 'Commercial Importer',
  NonCommercial = 'Non-Commercial',
  NonCommercialCommStream = 'Non-Commercial (Comm. Stream)',
  OtherGovermentDepartments = 'Other Government Departments',
}

export enum CarrierSubProgramNavigation {
  Air = 'air',
  CarrierFreightForwarder = 'carrier_freight_forward',
  Marine = 'marine',
  Rail = 'rail',
  Highway = 'highway'
}

export enum CarrierSubProgramType {
  Air = 'Air Carrier',
  CarrierFreightForwarder = 'Freight Forwarder',
  Marine = 'Marine Carrier',
  Rail = 'Rail Carrier',
  Highway = 'Highway Carrier'
}

export const mapCarrierSubProgramTypeToNavigation = new Map<string, CarrierSubProgramType>();
mapCarrierSubProgramTypeToNavigation[CarrierSubProgramType.Air] = CarrierSubProgramNavigation.Air;
mapCarrierSubProgramTypeToNavigation[CarrierSubProgramType.CarrierFreightForwarder] = CarrierSubProgramNavigation.CarrierFreightForwarder;
mapCarrierSubProgramTypeToNavigation[CarrierSubProgramType.Marine] = CarrierSubProgramNavigation.Marine;
mapCarrierSubProgramTypeToNavigation[CarrierSubProgramType.Rail] = CarrierSubProgramNavigation.Rail;
mapCarrierSubProgramTypeToNavigation[CarrierSubProgramType.Highway] = CarrierSubProgramNavigation.Highway;

export enum SubProgramEnrolmentAction {
  RppPayment = 'n',
  EnrolmentPayment = 'p',
  BondedApply = 'b',
  CarrierApply = 'ca'
}

export enum SubProgramEnrolmentType {
  Edi = 'edi',
  TrustedTrader = 'trusted_trader',
  ReleasePriorPayment = 'release_prior_payment',
  DutyReliefLicence = 'duty_relief_licence',
  CandianGoodsAbroad = 'canadian_goods_abroad',
  ContainerBank = 'container_bank',
  CoastingTradeLicence = 'coasting_trade_licence',
  DutyRemission = 'duty_remission',
  DutyRemissionOn120Basis = 'duty_remission_120_basis',
  ReportingSummaryAccounting = 'reporting_summary_accounting',
  Bonded = 'bonded',
  EdiServices = 'third_party'
}

export enum AirType {
  Cargo = 'Cargo',
  Passenger = 'Passenger'
}

export enum ProgramCategory {
  DutyFreeShop = 'Duty Free Shop',
  Warehouse = 'Warehouse',
  Bonded = 'Customs bonded warehouse',
  ThirdParty = 'third_party',
  ForeignEntity = 'Foreign Entity',
  SufferanceWarehouse = 'Sufferance warehouse',
  Broker = 'Broker'
}

export enum CarrierMode {
  Air = 'AIR',
  FreightForwarder = 'FF',
  Highway = 'HWY',
  Marine = 'MAR',
  Rail = 'RAL',
}

export enum EnrolmentType {
  New = 'Z02',
  Update = 'Z03',
  EDIService = 'Z04'
}

export enum CheckedOptions {
  Checked = 'X',
  UnChecked = ''
}

export enum S4CaseStatus {
  E0001 = 'E0001',
  E0002 = 'E0002',
  E0003 = 'E0003',
  E0004 = 'E0004',
  E0005 = 'E0005',
  E0007 = 'E0007',
  E0008 = 'E0008',
  E0012 = 'E0012',
  E0013 = 'E0013',
  E0014 = 'E0014',
  E0015 = 'E0015',
  E0016 = 'E0016',
  E0017 = 'E0017',
  E0018 = 'E0018',
  E0019 = 'E0019',
  E0024 = 'E0024',
  E0025 = 'E0025',
  E0026 = 'E0026',
  E0027 = 'E0027',
  E0028 = 'E0028',
  E0029 = 'E0029',
  E0030 = 'E0030',
  E0031 = 'E0031',
  E0032 = 'E0032',
  E0033 = 'E0033',
  E0034 = 'E0034',
  E0035 = 'E0035',
  E0036 = 'E0036',
  E0037 = 'E0037',
  E0038 = 'E0038',
  E0039 = 'E0039',
  E0040 = 'E0040',
  E0041 = 'E0041',
  E0042 = 'E0042',
  E0043 = 'E0043',
  E0044 = 'E0044',
  E0045 = 'E0045',
  E0046 = 'E0046',
  E0048 = 'E0048',
  ZR1 = 'ZR1',
  ZR2 = 'ZR2',
  ZR3 = 'ZR3',
  ZR4 = 'ZR4',
  ZR5 = 'ZR5'
}

// TODO: here below to be moved to other file

export enum HybrisStatusCodes {
  InProgress = 'HY001',
  PendingSecurityDeposit = 'HY002',
  Appealable = 'HY003',
  ErrorProcessingInterface = 'HY004',
  ErrorProcessingData = 'HY005',
  Completed = 'HY006',
  PendingFeePayment = 'HY007',
  Withdrawn = 'HY008',
  Rejected = 'HY009',
  Postpone = 'HY010',
  InDraft = 'HY011',
  Cancelled = 'HY012',
  Disenrolment = 'HY013',
  Compliant = 'HY014',
  ProgramReinstated = 'HY015',
  AppealableRuling = 'HY016',
  WithdrawalRequested = 'HY017',
  DocumentPending = 'HY018',
}

export const EnrolmentClosedCodes = [
  HybrisStatusCodes.Completed,
  HybrisStatusCodes.Rejected,
  HybrisStatusCodes.Withdrawn
];

export const EnrolmentErrorCodes = [
  HybrisStatusCodes.ErrorProcessingInterface,
  HybrisStatusCodes.ErrorProcessingData
];

export enum ProgramEnrolmentRequestSource {
  CbsaEnrolments = 'el',
  SubProgramEnrolments = 'sp',
  EnrolmentRequests = 'er'
}

export enum CaseTypes {
  ZIMP = 'ZIMP',
  ZBRO = 'ZBRO',
  ZCAR = 'ZCAR',
  ZWHB = 'ZWHB',
  ZWHS = 'ZWHS',
  ZEXP = 'ZEXP',
  ZDFS = 'ZDFS',
  Z3RD = 'Z3RD',
  ZEDI = 'ZEDI',
  ZAPP = 'ZAPP',
  ZRED = 'ZRED',
  ZTTA = 'ZTTA',
  ZSIA = 'ZSIA',
  ZDER = 'ZDER',
  ZPCP = 'ZPCP',
  ZREV = 'ZREV',
  ZVER = 'ZVER',
  ZRUL = 'ZRUL',
  ZMAS = 'ZMAS',
  ZMPR = 'ZMPR',
  ZCBD = 'ZCBD',
  ZCSA = 'ZCSA',
  ZSIM = 'ZSIM'
}

const allActionButtonsStates = new Map<String, IDisplayAction>
([
  // ==============
  // ENROLMENTS
  // ==============

  // IMPORTER
  [`${CaseTypes.ZIMP}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZIMP}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZIMP}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZIMP}_${HybrisStatusCodes.PendingSecurityDeposit}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZIMP}_${HybrisStatusCodes.PendingSecurityDeposit}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZIMP}_${HybrisStatusCodes.PendingSecurityDeposit}_${ACTIONTYPES.deposit}`, { isEnabled: true, isVisible: true }],

  [`${CaseTypes.ZIMP}_${HybrisStatusCodes.Appealable}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZIMP}_${HybrisStatusCodes.Appealable}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZIMP}_${HybrisStatusCodes.Appealable}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZIMP}_${HybrisStatusCodes.Appealable}_${ACTIONTYPES.appeal}`, { isEnabled: true, isVisible: true }],

  [`${CaseTypes.ZIMP}_${HybrisStatusCodes.ErrorProcessingInterface}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZIMP}_${HybrisStatusCodes.ErrorProcessingInterface}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZIMP}_${HybrisStatusCodes.ErrorProcessingInterface}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZIMP}_${HybrisStatusCodes.ErrorProcessingData}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZIMP}_${HybrisStatusCodes.ErrorProcessingData}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZIMP}_${HybrisStatusCodes.ErrorProcessingData}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZIMP}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZIMP}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZIMP}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZIMP}_${HybrisStatusCodes.Withdrawn}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZIMP}_${HybrisStatusCodes.Withdrawn}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZIMP}_${HybrisStatusCodes.Withdrawn}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZIMP}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZIMP}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZIMP}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  // CARRIER
  [`${CaseTypes.ZCAR}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZCAR}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZCAR}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZCAR}_${HybrisStatusCodes.PendingSecurityDeposit}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZCAR}_${HybrisStatusCodes.PendingSecurityDeposit}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZCAR}_${HybrisStatusCodes.PendingSecurityDeposit}_${ACTIONTYPES.deposit}`, { isEnabled: true, isVisible: true }],

  [`${CaseTypes.ZCAR}_${HybrisStatusCodes.Appealable}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZCAR}_${HybrisStatusCodes.Appealable}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZCAR}_${HybrisStatusCodes.Appealable}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZCAR}_${HybrisStatusCodes.Appealable}_${ACTIONTYPES.appeal}`, { isEnabled: true, isVisible: true }],

  [`${CaseTypes.ZCAR}_${HybrisStatusCodes.ErrorProcessingInterface}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZCAR}_${HybrisStatusCodes.ErrorProcessingInterface}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZCAR}_${HybrisStatusCodes.ErrorProcessingInterface}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZCAR}_${HybrisStatusCodes.ErrorProcessingData}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZCAR}_${HybrisStatusCodes.ErrorProcessingData}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZCAR}_${HybrisStatusCodes.ErrorProcessingData}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZCAR}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZCAR}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZCAR}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZCAR}_${HybrisStatusCodes.Withdrawn}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZCAR}_${HybrisStatusCodes.Withdrawn}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZCAR}_${HybrisStatusCodes.Withdrawn}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZCAR}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZCAR}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZCAR}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  // BROKER
  [`${CaseTypes.ZBRO}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZBRO}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZBRO}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.payment}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZBRO}_${HybrisStatusCodes.ErrorProcessingInterface}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZBRO}_${HybrisStatusCodes.ErrorProcessingInterface}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZBRO}_${HybrisStatusCodes.ErrorProcessingInterface}_${ACTIONTYPES.payment}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZBRO}_${HybrisStatusCodes.ErrorProcessingData}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZBRO}_${HybrisStatusCodes.ErrorProcessingData}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZBRO}_${HybrisStatusCodes.ErrorProcessingData}_${ACTIONTYPES.payment}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZBRO}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZBRO}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZBRO}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.payment}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZBRO}_${HybrisStatusCodes.PendingFeePayment}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZBRO}_${HybrisStatusCodes.PendingFeePayment}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZBRO}_${HybrisStatusCodes.PendingFeePayment}_${ACTIONTYPES.payment}`, { isEnabled: true, isVisible: true }],

  [`${CaseTypes.ZBRO}_${HybrisStatusCodes.Withdrawn}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZBRO}_${HybrisStatusCodes.Withdrawn}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZBRO}_${HybrisStatusCodes.Withdrawn}_${ACTIONTYPES.payment}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZBRO}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZBRO}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZBRO}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.payment}`, { isEnabled: false, isVisible: true }],

  // BONDED WAREHOUSE
  [`${CaseTypes.ZWHB}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZWHB}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZWHB}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZWHB}_${HybrisStatusCodes.PendingSecurityDeposit}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZWHB}_${HybrisStatusCodes.PendingSecurityDeposit}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZWHB}_${HybrisStatusCodes.PendingSecurityDeposit}_${ACTIONTYPES.deposit}`, { isEnabled: true, isVisible: true }],

  [`${CaseTypes.ZWHB}_${HybrisStatusCodes.ErrorProcessingInterface}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZWHB}_${HybrisStatusCodes.ErrorProcessingInterface}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZWHB}_${HybrisStatusCodes.ErrorProcessingInterface}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZWHB}_${HybrisStatusCodes.ErrorProcessingData}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZWHB}_${HybrisStatusCodes.ErrorProcessingData}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZWHB}_${HybrisStatusCodes.ErrorProcessingData}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZWHB}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZWHB}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZWHB}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZWHB}_${HybrisStatusCodes.Withdrawn}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZWHB}_${HybrisStatusCodes.Withdrawn}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZWHB}_${HybrisStatusCodes.Withdrawn}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZWHB}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZWHB}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZWHB}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  // SUFFERANCE WAREHOUSE
  [`${CaseTypes.ZWHS}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZWHS}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZWHS}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZWHS}_${HybrisStatusCodes.PendingSecurityDeposit}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZWHS}_${HybrisStatusCodes.PendingSecurityDeposit}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZWHS}_${HybrisStatusCodes.PendingSecurityDeposit}_${ACTIONTYPES.deposit}`, { isEnabled: true, isVisible: true }],

  [`${CaseTypes.ZWHS}_${HybrisStatusCodes.ErrorProcessingInterface}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZWHS}_${HybrisStatusCodes.ErrorProcessingInterface}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZWHS}_${HybrisStatusCodes.ErrorProcessingInterface}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZWHS}_${HybrisStatusCodes.ErrorProcessingData}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZWHS}_${HybrisStatusCodes.ErrorProcessingData}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZWHS}_${HybrisStatusCodes.ErrorProcessingData}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZWHS}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZWHS}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZWHS}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZWHS}_${HybrisStatusCodes.Withdrawn}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZWHS}_${HybrisStatusCodes.Withdrawn}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZWHS}_${HybrisStatusCodes.Withdrawn}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZWHS}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZWHS}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZWHS}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  // EXPORTER
  [`${CaseTypes.ZEXP}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZEXP}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZEXP}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZEXP}_${HybrisStatusCodes.ErrorProcessingInterface}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZEXP}_${HybrisStatusCodes.ErrorProcessingInterface}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZEXP}_${HybrisStatusCodes.ErrorProcessingInterface}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZEXP}_${HybrisStatusCodes.ErrorProcessingData}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZEXP}_${HybrisStatusCodes.ErrorProcessingData}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZEXP}_${HybrisStatusCodes.ErrorProcessingData}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZEXP}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZEXP}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZEXP}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZEXP}_${HybrisStatusCodes.Withdrawn}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZEXP}_${HybrisStatusCodes.Withdrawn}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZEXP}_${HybrisStatusCodes.Withdrawn}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZEXP}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZEXP}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZEXP}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  // DUTY FREE SHOP
  [`${CaseTypes.ZDFS}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZDFS}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZDFS}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZDFS}_${HybrisStatusCodes.PendingSecurityDeposit}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZDFS}_${HybrisStatusCodes.PendingSecurityDeposit}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZDFS}_${HybrisStatusCodes.PendingSecurityDeposit}_${ACTIONTYPES.deposit}`, { isEnabled: true, isVisible: true }],

  [`${CaseTypes.ZDFS}_${HybrisStatusCodes.ErrorProcessingInterface}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZDFS}_${HybrisStatusCodes.ErrorProcessingInterface}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZDFS}_${HybrisStatusCodes.ErrorProcessingInterface}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZDFS}_${HybrisStatusCodes.ErrorProcessingData}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZDFS}_${HybrisStatusCodes.ErrorProcessingData}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZDFS}_${HybrisStatusCodes.ErrorProcessingData}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZDFS}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZDFS}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZDFS}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZDFS}_${HybrisStatusCodes.Withdrawn}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZDFS}_${HybrisStatusCodes.Withdrawn}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZDFS}_${HybrisStatusCodes.Withdrawn}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZDFS}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZDFS}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZDFS}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  // THIRD PARTY
  [`${CaseTypes.Z3RD}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.Z3RD}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.Z3RD}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.Z3RD}_${HybrisStatusCodes.ErrorProcessingInterface}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.Z3RD}_${HybrisStatusCodes.ErrorProcessingInterface}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.Z3RD}_${HybrisStatusCodes.ErrorProcessingInterface}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.Z3RD}_${HybrisStatusCodes.ErrorProcessingData}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.Z3RD}_${HybrisStatusCodes.ErrorProcessingData}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.Z3RD}_${HybrisStatusCodes.ErrorProcessingData}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.Z3RD}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.Z3RD}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.Z3RD}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.Z3RD}_${HybrisStatusCodes.Withdrawn}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.Z3RD}_${HybrisStatusCodes.Withdrawn}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.Z3RD}_${HybrisStatusCodes.Withdrawn}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.Z3RD}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.Z3RD}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.Z3RD}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  // EDI
  [`${CaseTypes.ZEDI}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZEDI}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZEDI}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZEDI}_${HybrisStatusCodes.ErrorProcessingInterface}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZEDI}_${HybrisStatusCodes.ErrorProcessingInterface}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZEDI}_${HybrisStatusCodes.ErrorProcessingInterface}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZEDI}_${HybrisStatusCodes.ErrorProcessingData}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZEDI}_${HybrisStatusCodes.ErrorProcessingData}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZEDI}_${HybrisStatusCodes.ErrorProcessingData}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZEDI}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZEDI}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZEDI}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZEDI}_${HybrisStatusCodes.Withdrawn}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZEDI}_${HybrisStatusCodes.Withdrawn}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZEDI}_${HybrisStatusCodes.Withdrawn}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZEDI}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZEDI}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZEDI}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.deposit}`, { isEnabled: false, isVisible: true }],

  // ==============
  // APPEALS
  // ==============
  [`${CaseTypes.ZAPP}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.view}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZAPP}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZAPP}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZAPP}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.view}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZAPP}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZAPP}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZAPP}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.view}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZAPP}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZAPP}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZRED}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.view}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZRED}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZRED}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZRED}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.view}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZRED}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZRED}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZRED}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.view}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZRED}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZRED}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZSIA}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.view}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZSIA}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZSIA}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZSIA}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.view}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZSIA}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZSIA}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZSIA}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.view}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZSIA}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZSIA}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZTTA}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.view}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZTTA}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZTTA}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZTTA}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.view}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZTTA}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZTTA}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZTTA}_${HybrisStatusCodes.Cancelled}_${ACTIONTYPES.view}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZTTA}_${HybrisStatusCodes.Cancelled}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZTTA}_${HybrisStatusCodes.Cancelled}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],

  // ==============
  // BOND CLAIMS
  // ==============
  [`${CaseTypes.ZCBD}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZCBD}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZCBD}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZCBD}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZCBD}_${HybrisStatusCodes.Withdrawn}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZCBD}_${HybrisStatusCodes.Withdrawn}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],

  // ==============
  // COMPLIANCE
  // ==============
  [`${CaseTypes.ZPCP}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZPCP}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZPCP}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.viewComment}`, { isEnabled: false, isVisible: false }],
  [`${CaseTypes.ZPCP}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.addComment}`, { isEnabled: true, isVisible: true }],

  [`${CaseTypes.ZPCP}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZPCP}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZPCP}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.viewComment}`, { isEnabled: false, isVisible: false }],
  [`${CaseTypes.ZPCP}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.addComment}`, { isEnabled: true, isVisible: true }],

  [`${CaseTypes.ZPCP}_${HybrisStatusCodes.Cancelled}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZPCP}_${HybrisStatusCodes.Cancelled}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZPCP}_${HybrisStatusCodes.Cancelled}_${ACTIONTYPES.viewComment}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZPCP}_${HybrisStatusCodes.Cancelled}_${ACTIONTYPES.addComment}`, { isEnabled: false, isVisible: false }],

  [`${CaseTypes.ZPCP}_${HybrisStatusCodes.Disenrolment}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZPCP}_${HybrisStatusCodes.Disenrolment}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZPCP}_${HybrisStatusCodes.Disenrolment}_${ACTIONTYPES.appeal}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZPCP}_${HybrisStatusCodes.Disenrolment}_${ACTIONTYPES.viewComment}`, { isEnabled: false, isVisible: false }],
  [`${CaseTypes.ZPCP}_${HybrisStatusCodes.Disenrolment}_${ACTIONTYPES.addComment}`, { isEnabled: true, isVisible: true }],

  [`${CaseTypes.ZPCP}_${HybrisStatusCodes.Compliant}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZPCP}_${HybrisStatusCodes.Compliant}_+${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZPCP}_${HybrisStatusCodes.Compliant}_${ACTIONTYPES.viewComment}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZPCP}_${HybrisStatusCodes.Compliant}_${ACTIONTYPES.addComment}`, { isEnabled: false, isVisible: false }],

  [`${CaseTypes.ZPCP}_${HybrisStatusCodes.ProgramReinstated}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZPCP}_${HybrisStatusCodes.ProgramReinstated}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZPCP}_${HybrisStatusCodes.ProgramReinstated}_${ACTIONTYPES.viewComment}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZPCP}_${HybrisStatusCodes.ProgramReinstated}_${ACTIONTYPES.addComment}`, { isEnabled: false, isVisible: false }],

  [`${CaseTypes.ZVER}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZVER}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZVER}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.viewComment}`, { isEnabled: false, isVisible: false }],
  [`${CaseTypes.ZVER}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.addComment}`, { isEnabled: true, isVisible: true }],


  [`${CaseTypes.ZVER}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZVER}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZVER}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.viewComment}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZVER}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.addComment}`, { isEnabled: false, isVisible: false }],


  [`${CaseTypes.ZVER}_${HybrisStatusCodes.Cancelled}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZVER}_${HybrisStatusCodes.Cancelled}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZVER}_${HybrisStatusCodes.Cancelled}_${ACTIONTYPES.viewComment}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZVER}_${HybrisStatusCodes.Cancelled}_${ACTIONTYPES.addComment}`, { isEnabled: false, isVisible: false }],

  [`${CaseTypes.ZSIM}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZSIM}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZSIM}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.viewComment}`, { isEnabled: false, isVisible: false }],
  [`${CaseTypes.ZSIM}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.addComment}`, { isEnabled: true, isVisible: true }],

  [`${CaseTypes.ZSIM}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZSIM}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZSIM}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.viewComment}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZSIM}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.addComment}`, { isEnabled: false, isVisible: false }],

  [`${CaseTypes.ZSIM}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZSIM}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZSIM}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.viewComment}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZSIM}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.addComment}`, { isEnabled: false, isVisible: false }],


  [`${CaseTypes.ZSIM}_${HybrisStatusCodes.Cancelled}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZSIM}_${HybrisStatusCodes.Cancelled}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZSIM}_${HybrisStatusCodes.Cancelled}_${ACTIONTYPES.viewComment}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZSIM}_${HybrisStatusCodes.Cancelled}_${ACTIONTYPES.addComment}`, { isEnabled: false, isVisible: false }],

  // ==============
  // RULINGS
  // ==============
  [`${CaseTypes.ZRUL}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.form}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZRUL}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZRUL}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.withdraw}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZRUL}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.modify}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZRUL}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.form}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZRUL}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZRUL}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.appeal}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZRUL}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.modify}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZRUL}_${HybrisStatusCodes.Withdrawn}_${ACTIONTYPES.form}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZRUL}_${HybrisStatusCodes.Withdrawn}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZRUL}_${HybrisStatusCodes.Withdrawn}_${ACTIONTYPES.appeal}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZRUL}_${HybrisStatusCodes.Withdrawn}_${ACTIONTYPES.modify}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZRUL}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.form}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZRUL}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZRUL}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.appeal}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZRUL}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.modify}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZRUL}_${HybrisStatusCodes.Cancelled}_${ACTIONTYPES.form}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZRUL}_${HybrisStatusCodes.Cancelled}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZRUL}_${HybrisStatusCodes.Cancelled}_${ACTIONTYPES.modify}`, { isEnabled: false, isVisible: true }],

  [`${CaseTypes.ZRUL}_${HybrisStatusCodes.AppealableRuling}_${ACTIONTYPES.form}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZRUL}_${HybrisStatusCodes.AppealableRuling}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZRUL}_${HybrisStatusCodes.AppealableRuling}_${ACTIONTYPES.appeal}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZRUL}_${HybrisStatusCodes.AppealableRuling}_${ACTIONTYPES.modify}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZRUL}_${HybrisStatusCodes.AppealableRuling}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: false }],
  [`${CaseTypes.ZRUL}_${HybrisStatusCodes.AppealableRuling}_${ACTIONTYPES.withdraw}`, { isEnabled: false, isVisible: false }],

  // ==============
  // DECLARATIONS
  // ==============
  // drawbacks
  [`${CaseTypes.ZCSA}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZCSA}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZCSA}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZCSA}_${HybrisStatusCodes.DocumentPending}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],

  // reporting
  [`${CaseTypes.ZDER}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.download}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZDER}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.cancel}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZDER}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.download}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZDER}_${HybrisStatusCodes.Cancelled}_${ACTIONTYPES.download}`, { isEnabled: false, isVisible: true }],

  // mass adjustments
  [`${CaseTypes.ZMAS}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZMAS}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZMAS}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZMAS}_${HybrisStatusCodes.Postpone}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZMAS}_${HybrisStatusCodes.InDraft}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZMAS}_${HybrisStatusCodes.Cancelled}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],

  // master provisional
  [`${CaseTypes.ZMPR}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.documents}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZMPR}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZMPR}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZMPR}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZMPR}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZMPR}_${HybrisStatusCodes.Rejected}_${ACTIONTYPES.upload}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZMPR}_${HybrisStatusCodes.DocumentPending}_${ACTIONTYPES.upload}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZMPR}_${HybrisStatusCodes.DocumentPending}_${ACTIONTYPES.documents}`, { isEnabled: false, isVisible: true }],

  // ===============
  // RPP Compliance
  // ===============
  [`${CaseTypes.ZREV}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.documents}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZREV}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.viewComment}`, { isEnabled: false, isVisible: false }],
  [`${CaseTypes.ZREV}_${HybrisStatusCodes.InProgress}_${ACTIONTYPES.addComment}`, { isEnabled: true, isVisible: true }],

  [`${CaseTypes.ZREV}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.documents}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZREV}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.comment}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZREV}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.viewComment}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZREV}_${HybrisStatusCodes.Completed}_${ACTIONTYPES.addComment}`, { isEnabled: false, isVisible: false }],

  [`${CaseTypes.ZREV}_${HybrisStatusCodes.Cancelled}_${ACTIONTYPES.documents}`, { isEnabled: false, isVisible: true }],
  [`${CaseTypes.ZREV}_${HybrisStatusCodes.Cancelled}_${ACTIONTYPES.viewComment}`, { isEnabled: true, isVisible: true }],
  [`${CaseTypes.ZREV}_${HybrisStatusCodes.Cancelled}_${ACTIONTYPES.addComment}`, { isEnabled: false, isVisible: false }],

]);


export const isActionEnabled = (item: IHoldHybrisCaseStatus, action: ACTIONTYPES): boolean => {
  const buttonAction = getActionButton(item, action);
  return buttonAction?.isEnabled === true && buttonAction.isVisible === true;
}

export const isActionVisible = (item: IHoldHybrisCaseStatus, action: ACTIONTYPES): boolean => {
  const buttonAction = getActionButton(item, action);
  return buttonAction?.isVisible === true;
}

const getActionButton = (item: IHoldHybrisCaseStatus, action: ACTIONTYPES): IDisplayAction => {
  if ((item?.caseType === CaseTypes.ZCBD) && action !== ACTIONTYPES.appeal && action !== ACTIONTYPES.upload) { // todo: add to matrix
    return { isEnabled: true, isVisible: true };
  }

  let key = `${item?.caseType}_${item?.hybrisCaseStatus}_${action}`;
  return allActionButtonsStates.get(key);
}
